import React from "react";
import { Flex, Text } from "rebass/styled-components";
import styled from "styled-components";
import Countdown from "react-countdown";
import { AiOutlineClockCircle } from "react-icons/ai";
import { RiTimeLine } from "react-icons/ri";
import { useRecoilState } from "recoil";
import { useHistory } from "react-router-dom";

import theme from "theme/themeThree";
import { CountDownRenderer } from "MVVM/Views/Competitions/Views/Play/countdownTimer";
import { analytics } from "MVVM/Analytics";
import { StripePaystationState } from "MVVM/State";
import { ClipQuota } from "MVVM/Utilities/ClipLimits";

interface IClipEligibilityModalProps {
  clipQuota?: ClipQuota;
  userGroup?: string;
  onClose: () => void;
  closeQuickEditModal?: () => void;
}

const getTierCopy = (userGroup?: string) => {
  switch (userGroup) {
    case "ESSENTIALS":
      return {
        title: "You've Reached Your Clip Limit",
        body: (
          <>
            You've used all your clips for this period. Upgrade to Pro to create
            more clips and unlock additional features!
          </>
        ),
        buttonLabel: "Upgrade to Pro",
        lookUp: "pro_monthly",
        analyticsId: "clip_eligibility_upgrade_to_pro",
        showUpgradeButton: true,
      };
    case "PRO":
    case "PRO_PLUS":
    case "CREATOR_PLAT":
      return {
        title: "You've Reached Your Clip Limit",
        body: <>You've used all clips for your current plan.</>,
        buttonLabel: null,
        lookUp: null,
        analyticsId: null,
        showUpgradeButton: false,
      };
    case "FREE":
    default:
      return {
        title: "Clips are Limited on Free Plans",
        body: (
          <>
            Sorry, but Allstar is now a limited product on Free accounts.
            Upgrade to Essentials for just{" "}
            <Text as="span" fontWeight={600}>
              $0.99
            </Text>{" "}
            right now to create more clips!
          </>
        ),
        buttonLabel: "Upgrade to Essentials for $0.99",
        lookUp: "essentials_monthly",
        analyticsId: "clip_eligibility_upgrade_to_essentials",
        showUpgradeButton: true,
      };
  }
};

export const ClipEligibilityModal = ({
  clipQuota,
  userGroup,
  onClose,
  closeQuickEditModal,
}: IClipEligibilityModalProps) => {
  const [paystation, setPaystationOpen] = useRecoilState(StripePaystationState);
  const history = useHistory();
  const tierCopy = getTierCopy(userGroup);
  const nextAvailableAt = clipQuota?.nextAvailableAt;

  const handleUpgradeClick = () => {
    if (!tierCopy.lookUp || !tierCopy.analyticsId) return;

    analytics.track("Button - Click", {
      locationIdentifier: tierCopy.analyticsId,
      location: "clip_eligibility_modal",
    });

    onClose();
    closeQuickEditModal?.();

    setPaystationOpen({
      ...paystation,
      isModalOpen: true,
      type: "subscribe",
      lookUp: tierCopy.lookUp,
      returnUrl: window.location.pathname,
    });
  };

  const handleSeeAllPlansClick = () => {
    analytics.track("Button - Click", {
      locationIdentifier: "clip_eligibility_see_all_plans",
      location: "clip_eligibility_modal",
    });

    onClose();
    closeQuickEditModal?.();
    history.push("/upgrade");
  };

  return (
    <ModalContainer>
      <IconContainer>
        <ClockIcon />
      </IconContainer>

      <ContentSection>
        <Title variant="text.headingXSmall" color="ice">
          {tierCopy.title}
        </Title>

        <ReasonText variant="text.paragraphMedium" color="chalk">
          {tierCopy.body}
        </ReasonText>

        {nextAvailableAt && (
          <TimerSection>
            <Flex alignItems="center" style={{ gap: 6 }}>
              <TimerIcon />
              <Text variant="text.labelMedium" color="chalkAlpha80">
                Next free clip available in:
              </Text>
            </Flex>
            <Countdown
              date={new Date(nextAvailableAt)}
              renderer={({ days, hours, minutes, seconds }) => {
                if (days > 0) {
                  const timeString = `${hours < 10 ? "0" : ""}${hours}:${
                    minutes < 10 ? "0" : ""
                  }${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;

                  return (
                    <Flex alignItems="center" style={{ gap: 8 }}>
                      <Text
                        color="ice"
                        variant="text.headingSmall"
                        fontWeight={600}
                      >
                        {days} {days === 1 ? "day" : "days"}
                      </Text>
                      <Text
                        color="chalkAlpha60"
                        variant="text.labelMedium"
                        fontWeight={500}
                      >
                        {timeString}
                      </Text>
                    </Flex>
                  );
                }

                return (
                  <CountDownRenderer
                    days={days}
                    hours={hours}
                    minutes={minutes}
                    seconds={seconds}
                    color={theme.colors.ice}
                    variant="text.headingSmall"
                    showEndsIn={false}
                    showIcon={false}
                  />
                );
              }}
            />
          </TimerSection>
        )}
      </ContentSection>

      <ButtonContainer>
        <UpgradeButtonWrapper>
          {tierCopy.showUpgradeButton ? (
            <UpgradeButton onClick={handleUpgradeClick}>
              <Text variant="text.labelSmall" fontWeight={500} color="ice">
                {tierCopy.buttonLabel}
              </Text>
            </UpgradeButton>
          ) : null}
          <SeeAllPlansLink onClick={handleSeeAllPlansClick}>
            See all plans
          </SeeAllPlansLink>
        </UpgradeButtonWrapper>
      </ButtonContainer>
    </ModalContainer>
  );
};

const ModalContainer = styled(Flex)`
  align-items: center;
  background-color: ${theme.colors.grey850};
  border: 1px solid ${theme.colors.chalkAlpha20};
  border-radius: 12px;
  flex-direction: column;
  gap: 24px;
  max-width: 500px;
  padding: 32px;
  width: 100%;

  @media (max-width: ${(props) => props.theme.breaks.small}) {
    max-width: 90vw;
    padding: 24px;
  }
`;

const IconContainer = styled(Flex)`
  align-items: center;
  background: ${theme.colors.chalkAlpha10};
  border-radius: 50%;
  height: 64px;
  justify-content: center;
  width: 64px;
`;

const ClockIcon = styled(AiOutlineClockCircle)`
  color: ${theme.colors.ice};
  height: 32px;
  width: 32px;
`;

const TimerIcon = styled(RiTimeLine)`
  color: ${theme.colors.chalkAlpha80};
  height: 16px;
  width: 16px;
`;

const ContentSection = styled(Flex)`
  align-items: center;
  flex-direction: column;
  gap: 16px;
  text-align: center;
  width: 100%;
`;

const Title = styled(Text)`
  font-weight: 600;
  line-height: 1.2;
`;

const ReasonText = styled(Text)`
  line-height: 1.5;
  max-width: 400px;
`;

const TimerSection = styled(Flex)`
  align-items: center;
  background: ${theme.colors.chalkAlpha10};
  border-radius: 8px;
  flex-direction: column;
  gap: 12px;
  margin-top: 8px;
  padding: 20px;
  width: 100%;
`;

const ButtonContainer = styled(Flex)`
  align-items: stretch;
  flex-direction: column;
  gap: 12px;
  justify-content: center;
  width: 100%;

  @media (min-width: ${(props) => props.theme.breaks.small}) {
    align-items: flex-start;
    flex-direction: row;
  }
`;

const UpgradeButtonWrapper = styled(Flex)`
  align-items: center;
  flex: 1;
  flex-direction: column;
  flex-shrink: 0;
  gap: 8px;
`;

const UpgradeButton = styled(Flex)`
  align-items: center;
  background: ${theme.colors.envy};
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  color: ${theme.colors.ice};
  cursor: pointer;
  flex-shrink: 0;
  justify-content: center;
  padding: 10px 20px;
  text-align: center;
  transition: all 0.2s ease;
  white-space: nowrap;
  width: 100%;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: ${(props) => props.theme.breaks.small}) {
    padding: 8px 16px;
  }
`;

const SeeAllPlansLink = styled(Text)`
  color: ${theme.colors.chalkAlpha80};
  cursor: pointer;
  font-size: 12px;
  text-decoration: underline;
  transition: color 0.2s ease;

  &:hover {
    color: ${theme.colors.ice};
  }
`;
